import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import { getLocales } from 'assets/locales/Locale';
import { useEffect, useMemo } from 'react';
import ClaimDocumentPreview from '../../claim/other/Claim.DocumentPreview';
import { useClaimEdit } from '../ClaimEdit.Hooks';
import Theme from 'assets/themes/Theme.Common';
import claimInvoiceSaveApi from 'models/claims/claimInvoice/save/Api.ClaimInvoice.Save';
import claimInvoiceDeleteApi from 'models/claims/claimInvoice/delete/Api.ClaimInvoice.Delete';
import { HttpStatus } from 'config/Api.Config';
import { addLog } from 'assets/components/feedback/Feedback';
import { isEmpty } from 'lodash';

export default function ClaimEditInvoices() {
  const { lang } = getLocales();
  const { reload, claim, isClaimAdmin, isClaimInvoiceEditableByStatus, permissions } = useClaimEdit();

  const docForm = useForm<Utils.FormData<Model.IClaimInvoice & { formFiles: Model.IUploadableClaimInvoice[] }>>({
    default: { claimId: claim?.id, formFiles: [] },
    onSubmit: async (data) => {
      let totalFiles = 0;
      let totalUploadedFiles = 0;
      let totalFailedFiles = 0;
      for (const file of data.formFiles) {
        totalFiles += 1;
        if (file) {
          const res = await claimInvoiceSaveApi({
            ...data,
            formFile: file.formFile,
            name: file.newName,
            notes: file.notes,
          });
          if (res.status === HttpStatus.ok) totalUploadedFiles += 1;
          else totalFailedFiles += 1;
        }
      }
      addLog({
        success: `${totalUploadedFiles} out of ${totalFiles} files were uploaded successfully. ${totalFailedFiles} file(s) failed to upload. Please retry for any failed uploads if necessary.`,
      });
      docForm.reload();
      reload();
    },
    validation: (data, errors) => {
      if (!isEmpty(data.formFiles)) {
        data.formFiles.forEach((formFile, index) => {
          if (!errors.formFiles) errors.formFiles = {};
          if (!errors.formFiles[index]) errors.formFiles[index] = {};
          if (!formFile.newName) errors.formFiles[index].newName = lang.required;
        });
      }
    },
  });

  useEffect(() => {
    if (docForm?.data?.claimId !== claim?.id) {
      docForm.set({ claimId: claim?.id });
    }
  }, [claim?.id]);

  const onClaimInvoiceDelete = async (invoiceId: number) => {
    await claimInvoiceDeleteApi({ id: claim?.id, invoiceId });
    reload();
  };

  const formFiles = useMemo(
    () =>
      docForm.data.formFiles &&
      docForm.data.formFiles.map((it) => ({
        original: it.formFile,
        type: it.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(it.formFile),
        name: it.formFile.name,
        newName: it.newName,
        notes: it.notes,
      })),
    [docForm.data.formFiles]
  );

  const isDisabled = isClaimInvoiceEditableByStatus || docForm.hasErrors || docForm.isSubmitted || !docForm.isUpdated;

  return (
    <div style={{ marginLeft: Theme.Size.XL, marginRight: Theme.Size.XL }}>
      <h2>{lang.invoices}</h2>
      <ClaimDocumentPreview
        documents={claim?.claimInvoices?.map((it) => ({
          id: it.id,
          url: it.mediaFile.url,
          name: it.name,
          extension: it.mediaFile?.extension,
          mimeType: it.mediaFile?.contentType,
          storedFileName: it.mediaFile?.name,
          description: it.notes,
          info: lang.uploaded,
          onDelete: () => onClaimInvoiceDelete(it.id),
        }))}
        showGallery={true}
        isDeleteDisabled={isClaimInvoiceEditableByStatus || !(isClaimAdmin || permissions?.DELETE_CLAIMINVOICE)}
        isDownloadDisabled={!(isClaimAdmin || permissions?.DOWNLOAD_CLAIMINVOICE)}
      />
      <br />
      <FileInput
        name="files"
        fileLimit={5}
        value={formFiles}
        onChange={({ files }) => {
          const updatedFormFiles = files?.map((file) => ({
            formFile: file.original,
            newName: file.original?.name,
            ...file,
          }));
          docForm.update({ formFiles: updatedFormFiles });
        }}
        class={ThemeFileInputDropzone}
        error={docForm.errors.formFile?._objectError}
        label={lang.documentUpload}
        disabled={isClaimInvoiceEditableByStatus || !(isClaimAdmin || permissions?.UPLOAD_CLAIMINVOICE)}
        renderAfter={
          <>
            {formFiles?.map((file, index) => (
              <div key={index} style={{ width: '100%', margin: '10px auto' }}>
                <h6>{file?.name}</h6>
                <TextInput
                  name="newName"
                  label={lang.number}
                  value={file.newName}
                  error={docForm.errors?.formFiles[index]?.newName}
                  onChange={({ newName }) => {
                    const updatedFiles = [...docForm.data.formFiles];
                    updatedFiles[index].newName = newName;
                    docForm.update({ formFiles: updatedFiles });
                  }}
                  disabled={isClaimInvoiceEditableByStatus || !(isClaimAdmin || permissions?.UPLOAD_CLAIMINVOICE)}
                />
                <br />
                <MultilineTextInput
                  name="notes"
                  label={lang.notes}
                  value={file?.notes}
                  onChange={({ notes }) => {
                    const updatedFiles = [...docForm.data.formFiles];
                    updatedFiles[index].notes = notes;
                    docForm.update({ formFiles: updatedFiles });
                  }}
                  disabled={isClaimInvoiceEditableByStatus || !(isClaimAdmin || permissions?.UPLOAD_CLAIMINVOICE)}
                />
              </div>
            ))}
          </>
        }
      />
      <br />
      <Button
        render={lang.add}
        media={<Icon class="fas-upload" />}
        onClick={docForm.submit}
        disabled={isDisabled}
        active={!docForm.hasErrors}
        class={ThemeButtonCircle}
        style={{ marginLeft: 'auto' }}
      />
    </div>
  );
}
